import React, { useState, createContext, useCallback, useEffect } from 'react';
import PrimeReact from 'primereact/api';

export const LayoutContext = createContext({});

export const LayoutProvider = ({ children }) => {
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [selectedLoanUrl, setSelectedLoanUrl] = useState('');
    const [layoutConfig, setLayoutConfig] = useState({
        ripple: true,
        inputStyle: 'outlined',
        menuMode: 'static',
        colorScheme: 'light',
        theme: 'blue',
        scale: 14,
        menuTheme: 'darkgray',
        componentTheme: 'blue'
    });

    const [layoutState, setLayoutState] = useState({
        staticMenuDesktopInactive: false,
        overlayMenuActive: false,
        overlaySubmenuActive: false,
        profileSidebarVisible: false,
        configSidebarVisible: false,
        staticMenuMobileActive: false,
        menuHoverActive: false,
        searchBarActive: false,
        resetMenu: false,
        sidebarActive: false,
        anchored: true,
        rightMenuVisible: false
    });

    const onMenuToggle = useCallback(() => {
        // Handle mobile and tablet devices
        if (isMobile() || isTablet()) {
            setLayoutState((prevLayoutState) => ({
                ...prevLayoutState,
                staticMenuMobileActive: !prevLayoutState.staticMenuMobileActive,
                overlayMenuActive: false
            }));
        }
        // Handle desktop devices
        else if (isDesktop()) {
            if (isOverlay() || isDrawer()) {
                setLayoutState((prevLayoutState) => ({
                    ...prevLayoutState,
                    overlayMenuActive: !prevLayoutState.overlayMenuActive
                }));
            } else if (isReveal()) {
                setLayoutState((prevLayoutState) => ({
                    ...prevLayoutState,
                    sidebarActive: !prevLayoutState.sidebarActive
                }));
            } else if (isStatic() || isSlim()) {
                setLayoutState((prevLayoutState) => ({
                    ...prevLayoutState,
                    staticMenuDesktopInactive: !prevLayoutState.staticMenuDesktopInactive
                }));
            }
        }
    }, [layoutConfig.menuMode]);

    const hideOverlayMenu = useCallback(() => {
        setLayoutState((prevLayoutState) => ({
            ...prevLayoutState,
            overlayMenuActive: false,
            staticMenuMobileActive: false
        }));
    }, []);

    const showConfigSidebar = useCallback(() => {
        setLayoutState((prevLayoutState) => ({
            ...prevLayoutState,
            configSidebarVisible: true
        }));
    }, []);

    const hideConfigSidebar = useCallback(() => {
        setLayoutState((prevLayoutState) => ({
            ...prevLayoutState,
            configSidebarVisible: false
        }));
    }, []);

    const showProfileSidebar = useCallback(() => {
        setLayoutState((prevLayoutState) => ({
            ...prevLayoutState,
            profileSidebarVisible: true
        }));
    }, []);

    const hideProfileSidebar = useCallback(() => {
        setLayoutState((prevLayoutState) => ({
            ...prevLayoutState,
            profileSidebarVisible: false
        }));
    }, []);

    const showRightSidebar = useCallback(() => {
        setLayoutState((prevLayoutState) => ({
            ...prevLayoutState,
            rightMenuVisible: true
        }));
        hideOverlayMenu();
    }, [hideOverlayMenu]);

    const hideRightSidebar = useCallback(() => {
        setLayoutState((prevLayoutState) => ({
            ...prevLayoutState,
            rightMenuVisible: false
        }));
    }, []);

    const toggleSearch = useCallback(() => {
        setLayoutState((prevLayoutState) => ({
            ...prevLayoutState,
            searchBarActive: !prevLayoutState.searchBarActive
        }));
    }, []);

    const onSearchHide = useCallback(() => {
        setLayoutState((prevLayoutState) => ({
            ...prevLayoutState,
            searchBarActive: false
        }));
    }, []);

    const isOverlay = () => {
        return layoutConfig.menuMode === 'overlay';
    };

    const isSlim = () => {
        return layoutConfig.menuMode === 'slim';
    };

    const isCompact = () => {
        return layoutConfig.menuMode === 'compact';
    };

    const isHorizontal = () => {
        return layoutConfig.menuMode === 'horizontal';
    };
    
    const isStatic = () => {
        return layoutConfig.menuMode === 'static';
    };
    
    const isReveal = () => {
        return layoutConfig.menuMode === 'reveal';
    };
    
    const isDrawer = () => {
        return layoutConfig.menuMode === 'drawer';
    };

    const isDesktop = () => {
        return window.innerWidth > 991;
    };

    const isTablet = () => {
        return window.innerWidth >= 768 && window.innerWidth <= 991;
    };

    const isMobile = () => {
        return window.innerWidth < 768;
    };

    const isTouchDevice = () => {
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    };

    const onMenuProfileToggle = useCallback(() => {
        setLayoutState((prevLayoutState) => ({
            ...prevLayoutState,
            overlaySubmenuActive: !prevLayoutState.overlaySubmenuActive
        }));
    }, []);

    const changeTheme = (theme, colorScheme) => {
        const themeUrl = `/themes/${theme}/theme.css`;
        const themeElement = document.getElementById('theme-css');
        
        if (themeElement) {
            const newThemeUrl = themeElement.getAttribute('href').replace(/\/themes\/[^\/]+\/theme\.css/, themeUrl);
            
            replaceThemeLink(newThemeUrl, () => {
                setLayoutConfig((prevState) => ({
                    ...prevState,
                    theme,
                    colorScheme
                }));
            });
        }
    };

    const replaceThemeLink = (href, onComplete) => {
        const id = 'theme-css';
        const themeLink = document.getElementById(id);
        
        if (!themeLink) {
            console.error('Theme link element not found');
            return;
        }
        
        const cloneLinkElement = themeLink.cloneNode(true);

        cloneLinkElement.setAttribute('href', href);
        cloneLinkElement.setAttribute('id', id + '-clone');

        themeLink.parentNode.insertBefore(cloneLinkElement, themeLink.nextSibling);

        cloneLinkElement.addEventListener('load', () => {
            themeLink.remove();
            cloneLinkElement.setAttribute('id', id);
            onComplete && onComplete();
        });
    };

    const onConfigUpdate = useCallback((config) => {
        let _config = { ...config };
        
        // Update ripple effect
        if (_config.ripple !== undefined) {
            PrimeReact.ripple = _config.ripple;
            setLayoutConfig((prevState) => ({
                ...prevState,
                ripple: _config.ripple
            }));
        }
        
        // Update input style
        if (_config.inputStyle && _config.inputStyle !== layoutConfig.inputStyle) {
            setLayoutConfig((prevState) => ({
                ...prevState,
                inputStyle: _config.inputStyle
            }));
        }
        
        // Menu mode is locked to 'static' - ignore any attempts to change it
        // if (_config.menuMode && _config.menuMode !== layoutConfig.menuMode) {
        //     setLayoutConfig((prevState) => ({
        //         ...prevState,
        //         menuMode: _config.menuMode
        //     }));
        //     
        //     // Reset menu state when changing mode
        //     setLayoutState((prevLayoutState) => ({
        //         ...prevLayoutState,
        //         staticMenuDesktopInactive: false,
        //         overlayMenuActive: false,
        //         staticMenuMobileActive: false,
        //         menuHoverActive: false
        //     }));
        // }
        
        // Update color scheme and theme
        if (_config.colorScheme && _config.colorScheme !== layoutConfig.colorScheme) {
            const colorScheme = _config.colorScheme;
            const componentTheme = _config.componentTheme || layoutConfig.componentTheme || 'blue';
            let theme = `lara-${colorScheme}-${componentTheme}`;
            
            // Fallback to blue theme if specific theme doesn't exist
            if (!document.querySelector(`link[href*="${theme}"]`)) {
                theme = `lara-${colorScheme}-blue`;
            }
            
            changeTheme(theme, colorScheme);
            
            // Update document attribute for theme
            document.documentElement.setAttribute('data-theme', colorScheme);
        }
        
        // Update component theme
        if (_config.componentTheme && _config.componentTheme !== layoutConfig.componentTheme) {
            document.documentElement.setAttribute('data-theme-color', _config.componentTheme);
            
            // Update primary color CSS variable based on component theme
            const themeColors = {
                blue: '#3B82F6',
                green: '#10B981',
                purple: '#8B5CF6',
                orange: '#F59E0B',
                teal: '#14B8A6',
                pink: '#EC4899',
                indigo: '#6366F1',
                cyan: '#06B6D4'
            };
            
            if (themeColors[_config.componentTheme]) {
                document.documentElement.style.setProperty('--primary-color', themeColors[_config.componentTheme]);
            }
        }
        
        // Update scale
        if (_config.scale && _config.scale !== layoutConfig.scale) {
            document.documentElement.style.fontSize = _config.scale + 'px';
            setLayoutConfig((prevState) => ({
                ...prevState,
                scale: _config.scale
            }));
        }
        
        // Update menu theme
        if (_config.menuTheme && _config.menuTheme !== layoutConfig.menuTheme) {
            setLayoutConfig((prevState) => ({
                ...prevState,
                menuTheme: _config.menuTheme
            }));
        }
    }, [layoutConfig, changeTheme]);

    // Initialize theme and ripple on mount
    useEffect(() => {
        PrimeReact.ripple = layoutConfig.ripple;
        document.documentElement.style.fontSize = layoutConfig.scale + 'px';
        document.documentElement.setAttribute('data-theme', layoutConfig.colorScheme);
        document.documentElement.setAttribute('data-theme-color', layoutConfig.componentTheme);
        
        // Set initial primary color
        const themeColors = {
            blue: '#3B82F6',
            green: '#10B981',
            purple: '#8B5CF6',
            orange: '#F59E0B',
            teal: '#14B8A6',
            pink: '#EC4899',
            indigo: '#6366F1',
            cyan: '#06B6D4'
        };
        
        if (themeColors[layoutConfig.componentTheme]) {
            document.documentElement.style.setProperty('--primary-color', themeColors[layoutConfig.componentTheme]);
        }
    }, []);

    // Load saved configuration from localStorage
    useEffect(() => {
        const savedConfig = localStorage.getItem('layoutConfig');
        if (savedConfig) {
            try {
                const config = JSON.parse(savedConfig);
                onConfigUpdate(config);
            } catch (e) {
                console.error('Failed to load layout configuration', e);
            }
        }
    }, []);

    // Save configuration to localStorage
    useEffect(() => {
        localStorage.setItem('layoutConfig', JSON.stringify(layoutConfig));
    }, [layoutConfig]);

    const value = {
        layoutConfig,
        setLayoutConfig,
        layoutState,
        setLayoutState,
        onMenuToggle,
        showConfigSidebar,
        hideConfigSidebar,
        showProfileSidebar,
        hideProfileSidebar,
        showRightSidebar,
        hideRightSidebar,
        onMenuProfileToggle,
        onConfigUpdate,
        isSlim,
        isCompact,
        isHorizontal,
        isStatic,
        isReveal,
        isDrawer,
        isOverlay,
        isDesktop,
        isTablet,
        isMobile,
        isTouchDevice,
        breadcrumbs,
        setBreadcrumbs,
        toggleSearch,
        onSearchHide,
        hideOverlayMenu,
        selectedLoanUrl,
        setSelectedLoanUrl
    };

    return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};